import { capturePostHogEvent } from 'services/posthog.service';
import { POST_HOG_EVENT } from 'constants/events';
import { AUTH_SCREEN } from 'constants/general';
import { ReferralData } from 'interfaces/onboarding-flow.interface';

const INTERNAL_REFERRER_HOSTS = ['focusbear.io', 'localhost', '127.0.0.1'];

/**
 * Whether a referrer URL is one of our own hosts. Matches on the parsed hostname
 * rather than a substring so `focusbear.io.evil.com` / `evil.com/focusbear.io`
 * are correctly treated as external (CWE-20).
 */
export const isInternalReferrer = (referrer: string): boolean => {
  try {
    const { hostname } = new URL(referrer);
    return INTERNAL_REFERRER_HOSTS.some(
      (host) => hostname === host || hostname.endsWith(`.${host}`)
    );
  } catch {
    return false;
  }
};

/**
 * Tracks external referrals and landing page redirects
 * Handles multiple referral sources:
 * - Landing page redirects (ui=signup)
 * - External site referrals (HTTP referrer)
 * - UTM parameters (utm_source, utm_medium, utm_campaign)
 * - Custom referral codes (ref, referral)
 */
export const trackReferralSource = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const referrer = document.referrer;

  // Check if coming from landing page (ui=signup parameter)
  const uiParam = urlParams.get('ui');
  const isLandingPageReferral = uiParam === AUTH_SCREEN.SIGN_UP;

  // Check for UTM parameters
  const utmSource = urlParams.get('utm_source');
  const utmMedium = urlParams.get('utm_medium');
  const utmCampaign = urlParams.get('utm_campaign');

  // Check for custom referral parameters
  const ref = urlParams.get('ref');
  const referral = urlParams.get('referral');

  // Check if coming from external site (not focusbear.io)
  const isExternalReferral = Boolean(referrer) && !isInternalReferrer(referrer);

  // Determine referral source
  let source = 'direct';
  let medium = 'none';
  let campaign = 'none';

  if (isLandingPageReferral) {
    source = 'landing-page';
    medium = 'website';
    campaign = 'landing-page-redirect';
  } else if (utmSource) {
    source = utmSource;
    medium = utmMedium || 'unknown';
    campaign = utmCampaign || 'unknown';
  } else if (ref) {
    source = 'referral-code';
    medium = 'referral';
    campaign = ref;
  } else if (referral) {
    source = 'referral-code';
    medium = 'referral';
    campaign = referral;
  } else if (isExternalReferral) {
    source = 'external-site';
    medium = 'referral';
    campaign = 'external-referral';
  }

  const referralData: ReferralData = {
    source,
    medium,
    campaign,
    referrer: isExternalReferral ? referrer : undefined,
    landingPage: isLandingPageReferral,
    utmSource: utmSource || undefined,
    utmMedium: utmMedium || undefined,
    utmCampaign: utmCampaign || undefined,
    ref: ref || undefined,
    referral: referral || undefined
  };

  // Fire appropriate events based on referral type
  if (isLandingPageReferral) {
    capturePostHogEvent(
      POST_HOG_EVENT.AUTH_PAGE.LANDING_PAGE_REFERRAL,
      referralData
    );
  } else if (isExternalReferral || utmSource || ref || referral) {
    capturePostHogEvent(
      POST_HOG_EVENT.AUTH_PAGE.EXTERNAL_REFERRAL_LANDED,
      referralData
    );
  }

  return referralData;
};

/**
 * Gets referral data without firing events (for use in other components)
 */
export const getReferralData = (): ReferralData => {
  const urlParams = new URLSearchParams(window.location.search);
  const referrer = document.referrer;

  const uiParam = urlParams.get('ui');
  const isLandingPageReferral = uiParam === AUTH_SCREEN.SIGN_UP;

  const utmSource = urlParams.get('utm_source');
  const utmMedium = urlParams.get('utm_medium');
  const utmCampaign = urlParams.get('utm_campaign');

  const ref = urlParams.get('ref');
  const referral = urlParams.get('referral');

  const isExternalReferral = Boolean(referrer) && !isInternalReferrer(referrer);

  let source = 'direct';
  let medium = 'none';
  let campaign = 'none';

  if (isLandingPageReferral) {
    source = 'landing-page';
    medium = 'website';
    campaign = 'landing-page-redirect';
  } else if (utmSource) {
    source = utmSource;
    medium = utmMedium || 'unknown';
    campaign = utmCampaign || 'unknown';
  } else if (ref) {
    source = 'referral-code';
    medium = 'referral';
    campaign = ref;
  } else if (referral) {
    source = 'referral-code';
    medium = 'referral';
    campaign = referral;
  } else if (isExternalReferral) {
    source = 'external-site';
    medium = 'referral';
    campaign = 'external-referral';
  }

  return {
    source,
    medium,
    campaign,
    referrer: isExternalReferral ? referrer : undefined,
    landingPage: isLandingPageReferral,
    utmSource: utmSource || undefined,
    utmMedium: utmMedium || undefined,
    utmCampaign: utmCampaign || undefined,
    ref: ref || undefined,
    referral: referral || undefined
  };
};
